import clsx from 'clsx';
import { Link, useFetcher } from '~/remix';
import Button from './Button';

export default function Pricing({
  hideHeader = false,
}: {
  hideHeader?: boolean;
}) {
  const fetcher = useFetcher();

  return (
    <div className="pricing-container mx-auto mb-4 flex max-w-5xl flex-col items-center text-center">
      <h1 className="text-3xl font-bold text-ssm-purple">
        Choose your package:
      </h1>
      <div className="mt-4 flex w-full flex-col items-center rounded-2xl bg-ssm-orange-400 p-6 text-center text-white shadow-lg shadow-accent-dark/30">
        <h2 className="text-2xl font-bold sm:text-4xl">
          Every Message Includes:
        </h2>
        <div className="mt-4 flex flex-col flex-wrap items-center gap-4 text-xl sm:flex-row">
          <div>Unlimited Recipients</div>
          <div className="hidden sm:block">•</div>
          <div>10,000 Words</div>
          <div className="hidden sm:block">•</div>
          <div>5 Photos</div>
        </div>
        <div className="mt-4 flex flex-col flex-wrap items-center gap-4 text-xl sm:flex-row">
          <div>1 Audio Recording</div>
          <div className="hidden sm:block">•</div>
          <div>1 Video</div>
        </div>
      </div>
      <div className="mt-6 flex w-full flex-col flex-wrap items-center gap-6 sm:flex-row sm:gap-8">
        <PricingCard
          packageName="Standard"
          messageCount={3}
          price={99}
          pricePerMessage={33}
          savings={15}
          textColor="text-ssm-purple"
          bgColor="bg-ssm-purple"
          buttonVariant="secondary"
        ></PricingCard>
        <div className="hidden flex-1 lg:block">
          <PricingCard
            packageName="Sunset"
            messageCount={12}
            price={299}
            pricePerMessage={25}
            savings={36}
            textColor="text-ssm-orange-400"
            bgColor="bg-ssm-orange-400"
            buttonVariant="primary"
          >
            <div className="bg-ssm-orange-400 pb-4 pt-4 text-2xl font-bold text-white">
              Best Value
            </div>
          </PricingCard>
        </div>
        <PricingCard
          packageName="Gold"
          messageCount={5}
          price={149}
          pricePerMessage={30}
          savings={24}
          textColor="text-ssm-purple"
          bgColor="bg-ssm-purple"
          buttonVariant="secondary"
        ></PricingCard>
        <div className="flex-1 lg:hidden">
          <PricingCard
            packageName="Sunset"
            messageCount={12}
            price={299}
            pricePerMessage={25}
            savings={36}
            textColor="text-ssm-orange-400"
            bgColor="bg-ssm-orange-400"
            buttonVariant="primary"
          >
            <div className="bg-ssm-orange-400 pb-4 pt-4 text-2xl font-bold text-white">
              Best Value
            </div>
          </PricingCard>
        </div>
      </div>
      <fetcher.Form method="post" action="/app/messages/select-package">
        <input type="hidden" name="packageName" value="Single" />
        <input type="hidden" name="packageMessageCount" value={1} />
        <button className="mt-4 text-lg text-ssm-purple underline">
          Only need 1 Message?
          <br className="sm:hidden" />
          <span> </span>It's just <sup>$</sup>39. Click here.
        </button>
      </fetcher.Form>
      <div className="mt-8 rounded-xl bg-secondary-darker px-6 py-6 shadow-lg sm:px-16">
        <p className="text-xl font-bold text-white sm:text-2xl">
          Custom Timelines Coming Soon
        </p>
        <p className="mt-2 text-lg font-bold text-white">
          Deliver messages on anniversaries, birthdays, etc.
        </p>
        <p className="mt-2 flex justify-center text-3xl font-bold text-amber-400">
          (<span className="inline-block text-xl">$</span>
          <span>Priceless</span>)
        </p>
      </div>
    </div>
  );
}

function PricingCard({
  packageName,
  messageCount,
  price,
  pricePerMessage,
  savings,
  textColor,
  bgColor,
  buttonVariant,
  margin,
  children,
}: {
  packageName: string;
  messageCount: number;
  price: number;
  pricePerMessage: number;
  savings: number;
  textColor: string;
  bgColor: string;
  buttonVariant: 'primary' | 'secondary';
  margin?: string;
  children?: any;
}) {
  const fetcher = useFetcher();

  return (
    <div
      className={clsx(
        textColor,
        bgColor,
        margin,
        'flex-1 overflow-clip rounded-xl shadow-lg shadow-accent-dark/30 sm:min-w-[33%] lg:min-w-0',
      )}
    >
      <fetcher.Form method="post" action="/app/messages/select-package">
        <input type="hidden" name="packageName" value={packageName} />
        <input type="hidden" name="packageMessageCount" value={messageCount} />
        <div className={clsx(bgColor, 'p-4 text-white')}>
          <h2 className="whitespace-nowrap text-center text-3xl font-bold leading-tight text-white">
            {packageName} Package
          </h2>
          <h3 className="text-center text-2xl font-bold leading-tight text-white">
            {messageCount} Messages
          </h3>
        </div>
        <div className="flex flex-col items-center bg-white p-4 text-left text-xl">
          <div className="mt-6 text-4xl font-bold">
            <sup>$</sup>
            {price}
          </div>
          <div className="mt-4 text-center">
            <div className="text-xl">
              <sup>$</sup>
              {pricePerMessage} per message
            </div>
            <div className="text-base">{savings}% Savings</div>
          </div>
          <div className="my-4 flex flex-col items-center gap-4">
            <Button type="submit" size="lg" variant={buttonVariant}>
              Select
            </Button>

            <Link
              to={`/packages/info?packageName=${packageName}&messageCount=${messageCount}`}
              className="text-base text-ssm-purple underline hover:text-ssm-purple/70"
            >
              More Info
            </Link>
          </div>
        </div>
        {children}
      </fetcher.Form>
    </div>
  );
}
